import { isAnyOf } from '@reduxjs/toolkit';

import type {
  BuilderDaSimulacao,
  ExecucaoDaSimulacao,
  ExecucoesNoEstado,
  NegociacaoSimulacaoState,
  PaginaDePassos,
  PassosNoEstado,
} from '../../types';
import { iniciarSimulacaoDoAdmin } from '../admin/actions';
import { registrarResultadoDaAssinatura } from '../assinatura/actions';
import { executarEntrada } from '../entrada/actions';
import { execucoesVazias, passosVazios } from '../estadoInicial';
import {
  estadoDaSimulacaoRecebido,
  passosDaExecucaoFechados,
  progressoDoAvancoRecebido,
} from '../eventosDoStore';
import { avancarRelogioDaSimulacao, continuarAvancoDoRelogio } from '../relogio/actions';
import { buscarExecucoes, buscarPassos } from './actions';

/** O que chega por último vale para o mesmo id: uma execução em curso termina numa releitura. */
const juntarPorId = <T extends { id: string }>(primeiros: T[], depois: T[]): T[] => {
  const ids = new Set(primeiros.map((item) => item.id));
  return [...primeiros, ...depois.filter((item) => !ids.has(item.id))];
};

const execucoesDaTela = (state: NegociacaoSimulacaoState) =>
  state.execucoes.simulacaoId === state.simulacao?.id;

type CargaDeExecucoes = 'requisicaoDaPrimeiraPagina' | 'requisicaoDoCursor';

const cargaDe = (cursor: string | null): CargaDeExecucoes =>
  cursor === null ? 'requisicaoDaPrimeiraPagina' : 'requisicaoDoCursor';

const encerrarCarga = (execucoes: ExecucoesNoEstado, carga: CargaDeExecucoes) => {
  execucoes[carga] = null;
  execucoes.carregando =
    execucoes.requisicaoDaPrimeiraPagina !== null || execucoes.requisicaoDoCursor !== null;
};

/**
 * A releitura da primeira página põe as execuções novas no topo. Com páginas antigas já
 * carregadas, ou com o "Carregar mais" em voo, as que estão na lista ficam e o cursor continua o
 * delas: o cursor em voo segue a lista anterior, e trocá-la pela primeira página nova perderia as
 * execuções entre as duas.
 */
const receberExecucoes = (
  state: NegociacaoSimulacaoState,
  itens: ExecucaoDaSimulacao[],
  pagina: { nextCursor: string | null; hasMore: boolean },
  cursor: string | null,
) => {
  const { execucoes } = state;
  const manterAntigas = execucoes.carregouMais || execucoes.requisicaoDoCursor !== null;
  if (cursor !== null) {
    execucoes.itens = juntarPorId(execucoes.itens, itens);
    execucoes.carregouMais = true;
  } else {
    execucoes.itens = manterAntigas ? juntarPorId(itens, execucoes.itens) : itens;
  }
  if (cursor !== null || !manterAntigas) {
    execucoes.nextCursor = pagina.nextCursor;
    execucoes.hasMore = pagina.hasMore;
  }
};

type CargaDePassos = 'requisicaoDaPrimeiraPagina' | 'requisicaoDaPaginaSeguinte';

const cargaDosPassos = (page: number): CargaDePassos =>
  page === 1 ? 'requisicaoDaPrimeiraPagina' : 'requisicaoDaPaginaSeguinte';

const encerrarCargaDosPassos = (passos: PassosNoEstado, carga: CargaDePassos) => {
  passos[carga] = null;
  passos.carregando =
    passos.requisicaoDaPrimeiraPagina !== null || passos.requisicaoDaPaginaSeguinte !== null;
};

/**
 * O core grava os passos no fim de cada rodada, e a execução WAITING retomada mantém o id: os
 * passos novos chegam depois dos já carregados, e não na página 1. Por isso, com páginas
 * seguintes na lista, a releitura da página 1 só troca os itens dela, `page` continua a última
 * carregada e `hasNext` sai do total novo. O `hasNext` da página 1 deixaria os passos novos fora
 * de alcance, ou ofereceria "Carregar mais" com tudo já na lista.
 */
const receberPassos = (passos: PassosNoEstado, pagina: PaginaDePassos) => {
  passos.total = pagina.total;
  passos.pageSize = pagina.pageSize;
  if (pagina.page === 1 && passos.page > 1) {
    passos.itens = juntarPorId(pagina.items, passos.itens);
    passos.hasNext = passos.itens.length < pagina.total;
    return;
  }
  passos.itens = pagina.page === 1 ? pagina.items : juntarPorId(passos.itens, pagina.items);
  passos.page = pagina.page;
  passos.hasNext = pagina.hasNext;
};

/** Comandos que fazem o motor rodar, concluídos ou não: os logs se releem (CA-7). */
const comandoTerminado = isAnyOf(
  iniciarSimulacaoDoAdmin.fulfilled,
  executarEntrada.fulfilled,
  executarEntrada.rejected,
  progressoDoAvancoRecebido,
  avancarRelogioDaSimulacao.rejected,
  continuarAvancoDoRelogio.rejected,
  registrarResultadoDaAssinatura.fulfilled,
  registrarResultadoDaAssinatura.rejected,
  estadoDaSimulacaoRecebido,
);

/** Vai por último no builder: o `addMatcher` só pode vir depois de todos os `addCase`. */
export function registrarLogsDaSimulacao(builder: BuilderDaSimulacao): void {
  builder
    .addCase(buscarExecucoes.pending, (state, action) => {
      const simulacaoId = state.simulacao?.id ?? null;
      if (!execucoesDaTela(state)) state.execucoes = execucoesVazias(simulacaoId);
      state.execucoes.carregando = true;
      state.execucoes.erro = null;
      state.execucoes[cargaDe(action.meta.arg.cursor)] = action.meta.requestId;
    })
    .addCase(buscarExecucoes.fulfilled, (state, action) => {
      const carga = cargaDe(action.meta.arg.cursor);
      if (
        state.execucoes[carga] !== action.meta.requestId ||
        action.payload.simulacaoId !== state.simulacao?.id
      )
        return;
      const { pagina } = action.payload;
      receberExecucoes(state, pagina.items, pagina, action.meta.arg.cursor);
      encerrarCarga(state.execucoes, carga);
    })
    .addCase(buscarExecucoes.rejected, (state, action) => {
      const carga = cargaDe(action.meta.arg.cursor);
      if (state.execucoes[carga] !== action.meta.requestId) return;
      encerrarCarga(state.execucoes, carga);
      state.execucoes.erro = action.payload ?? null;
    })
    .addCase(buscarPassos.pending, (state, action) => {
      if (state.passos.execucaoId !== action.meta.arg.execucaoId)
        state.passos = passosVazios(action.meta.arg.execucaoId);
      state.passos.carregando = true;
      state.passos.erro = null;
      state.passos[cargaDosPassos(action.meta.arg.page)] = action.meta.requestId;
    })
    .addCase(buscarPassos.fulfilled, (state, action) => {
      const carga = cargaDosPassos(action.meta.arg.page);
      if (
        state.passos[carga] !== action.meta.requestId ||
        state.passos.execucaoId !== action.payload.execucaoId ||
        action.payload.simulacaoId !== state.simulacao?.id
      )
        return;
      receberPassos(state.passos, action.payload.pagina);
      encerrarCargaDosPassos(state.passos, carga);
    })
    .addCase(buscarPassos.rejected, (state, action) => {
      const carga = cargaDosPassos(action.meta.arg.page);
      if (state.passos[carga] !== action.meta.requestId) return;
      encerrarCargaDosPassos(state.passos, carga);
      state.passos.erro = action.payload ?? null;
    })
    .addCase(passosDaExecucaoFechados, (state) => {
      state.passos = passosVazios(null);
    })
    .addMatcher(comandoTerminado, (state) => {
      if (state.superficie === 'ADMIN') state.marcoDosLogs += 1;
    });
}
